<script setup>
import { computed } from 'vue';

// Fila de ranking con barra horizontal proporcional (estilo
// Plausible): la barra es un fill de fondo detrás del label, el
// count y el % van a la derecha con tabular numerals. Componente
// puramente presentacional — reutilizado por TopPages, Referrers,
// Tech y Audience.

const props = defineProps({
  label: { type: String, required: true },
  count: { type: Number, required: true },
  // Porcentaje 0-100 ya calculado por el caller (count/total).
  pct: { type: Number, default: null },
  mono: { type: Boolean, default: false },
});

const barWidth = computed(() => {
  if (props.pct == null || Number.isNaN(props.pct)) return '0%';
  return `${Math.min(100, Math.max(0, props.pct))}%`;
});

const pctLabel = computed(() => {
  if (props.pct == null || Number.isNaN(props.pct)) return null;
  return `${props.pct < 10 ? props.pct.toFixed(1) : Math.round(props.pct)}%`;
});

const countLabel = computed(() => props.count.toLocaleString('en-US'));
</script>

<template>
  <div class="brr">
    <div
      class="brr__bar"
      :style="{ width: barWidth }"
      aria-hidden="true"
    />
    <div class="brr__content">
      <span
        :class="['brr__label', { 'is-mono': mono }]"
        :title="label"
      >{{ label }}</span>
      <slot />
      <span class="brr__count">{{ countLabel }}</span>
      <span
        v-if="pctLabel"
        class="brr__pct"
      >{{ pctLabel }}</span>
    </div>
  </div>
</template>

<style scoped>
.brr {
  position: relative;
  border-radius: var(--radius-md);
  background: var(--bg-subtle);
  overflow: hidden;
}

.brr__bar {
  position: absolute;
  inset: auto auto 0 0;
  height: var(--space-1);
  background: var(--chart-1);
  opacity: 0.65;
  border-radius: var(--radius-sm);
}

.brr__content {
  position: relative;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3);
  font-size: var(--text-sm);
}

.brr__label {
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
  color: var(--fg-primary);
}

.brr__label.is-mono {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
}

.brr__count {
  font-feature-settings: "tnum";
  color: var(--fg-primary);
  font-weight: var(--fw-medium);
  flex-shrink: 0;
}

.brr__pct {
  font-feature-settings: "tnum";
  color: var(--fg-secondary);
  font-size: var(--text-xs);
  min-width: 2.5rem;
  text-align: right;
}
</style>
